/**
 * OFBiz 新接口 → 旧前端 PascalCase 结构的共享适配器
 * ------------------------------------------------------------------
 * 新接口 /monitor/tree 返回 camelCase + children/monitors 嵌套结构，
 * 旧前端代码（formatTreeData / getDeletedGroupIds / 各组件）期望的是
 * PascalCase + GroupEquipment/EquipmentMonitor 嵌套结构。
 * 多个 action（deviceMonitorTree_action、equipment_action）都要用到这个转换，
 * 提取到这里统一维护。
 */

import { classEnumToEquipmentType } from './equipmentTypeMap';

/**
 * 把新接口 /monitor/tree 的返回结构适配成旧 EccGroup 扁平数组结构。
 *
 * 新接口返回嵌套树（equipments 里既有子组也有设备）：
 *   [{ recId, nodeType:'GROUP', equipments: [
 *       { recId, nodeType:'GROUP', equipments:[...] },   ← 子组
 *       { recId, nodeType:'EQUIPMENT', monitors:[...] }  ← 设备
 *   ]}]
 *
 * 旧 OData 是扁平结构：所有组平铺在数组里（用 ParentGroupId 表示父子），
 * 设备嵌套在父组的 GroupEquipment 里。formatTreeData 据此组装树。
 * 所以这里必须把嵌套树展平：子组平铺到数组顶层，设备放进父组 GroupEquipment。
 *
 * @param {Array} tree 新接口返回的 tree 数组
 * @returns {Array} 旧扁平格式数组（组用 ParentGroupId 关联）
 */
export function adaptMonitorTreeToLegacy(tree) {
  if (!Array.isArray(tree)) return [];
  const result = [];
  // 递归展平：遍历树，把每个组平铺到 result，设备放进父组的 GroupEquipment
  const flatten = (groups, result) => {
    groups.forEach((group) => {
      const g = group || {};
      const items = g.equipments || [];
      // 子组来源一：独立的 groups 数组（后端当前结构）
      // 子组来源二：equipments 里 nodeType==='GROUP' 的节点（兼容旧混合结构）
      const subGroups = Array.isArray(g.groups) ? g.groups.slice() : [];
      const equipments = [];
      items.forEach((child) => {
        const nt = (child && (child.nodeType || child.NodeType) || '').toUpperCase();
        if (nt === 'GROUP') {
          subGroups.push(child);
        } else {
          equipments.push(adaptEquipToLegacy(child));
        }
      });
      const legacyGroup = {
        RecId: g.recId || g.groupId || g.RecId,
        Title: g.title || g.groupName || g.Title,
        GroupName: g.groupName || g.title || g.GroupName,
        NodeType: g.nodeType || 'GROUP',
        // 旧 EccGroup 契约为 Status：组列表 statusFormatter 用 record.Status 区分组/资源
        // 图标并取状态色，启用/禁用按钮也按它置灰；MonitorStatus 保留供树组件兜底链使用
        Status: g.status || g.monitorStatus || g.Status || '',
        MonitorStatus: g.status || g.monitorStatus || '',
        ParentGroupId: g.parentGroupId !== undefined && g.parentGroupId !== null
          ? g.parentGroupId
          : (g.ParentGroupId !== undefined && g.ParentGroupId !== null ? g.ParentGroupId : ''),
        Sorter: g.sorter || g.Sorter || '',
        GroupEquipment: equipments,
      };
      result.push(legacyGroup);
      // 递归处理子组（平铺到 result 同一层）
      if (subGroups.length) {
        flatten(subGroups, result);
      }
    });
  };
  flatten(tree, result);
  return result;
}

/** 转换单个分组节点（含子设备、子监测器） */
export function adaptGroupToLegacy(group) {
  const g = group || {};
  // 设备：优先取 equipments，兼容旧 children 结构
  const equipArray = g.equipments || g.children || g.GroupEquipment || [];
  return {
    RecId: g.recId || g.groupId || g.RecId,
    Title: g.title || g.groupName || g.Title,
    GroupName: g.groupName || g.title || g.GroupName,
    NodeType: g.nodeType || 'GROUP',
    // 旧 EccGroup 契约为 Status：组列表 statusFormatter 用 record.Status 区分组/资源
    // 图标并取状态色，启用/禁用按钮也按它置灰
    Status: g.status || g.monitorStatus || g.Status || '',
    // ⚠️ parentGroupId 后端返回 ""（空字符串=顶级分组）。
    // 不能用 ||（空字符串是 falsy 会被吞成 undefined），
    // 否则 formatTreeData 的 ParentGroupId === '' 判断会失败，导致树不渲染。
    ParentGroupId: g.parentGroupId !== undefined && g.parentGroupId !== null
      ? g.parentGroupId
      : (g.ParentGroupId !== undefined && g.ParentGroupId !== null ? g.ParentGroupId : ''),
    Sorter: g.sorter || g.Sorter || '',
    GroupEquipment: equipArray.map((equip) => adaptEquipToLegacy(equip)),
  };
}

/**
 * 规范化"是否禁用"字段（新增监测器 IS_DISABLED 可能为布尔 false/true，
 * 也可能为字符串 'false'/'true'/'0'/'1'/'Y'/'N'/'')。
 * 注意：字符串 'false' 是 truthy，直接用 `||` 拼接会误判为禁用，必须规范化。
 */
export function normalizeDisabled(value) {
  if (value === true || value === 1) return true;
  if (value === 'true' || value === '1' || value === 'Y' || value === 'y') return true;
  return false;
}

/** 转换单个设备节点（含监测器） */
export function adaptEquipToLegacy(equip) {
  const e = equip || {};
  const monitors = e.monitors || e.EquipmentMonitor || [];
  // classEnumId → 旧 EquipmentType（供 formatTreeData 设 eqType、getResourceView 匹配类型）
  var equipmentType = e.EquipmentType || classEnumToEquipmentType(e.classEnumId) || '';
  return {
    RecId: e.recId || e.equipmentId || e.RecId,
    Title: e.title || e.fixedAssetName || e.nodeName || e.Title,
    NodeType: e.nodeType || 'EQUIPMENT',
    EquipmentType: equipmentType,
    // 设备真实类型码（EQUIP_TYPE，如 Equipment.Windows）：优先取扩展属性，
    // 用于新增监测器时按 /monitor/types 的 equipType 过滤模板
    equipType: (e.attributes && e.attributes.EQUIP_TYPE) || e.equipType || '',
    // 旧 UI 组件（resourceSetting_basicInfo_*）用 relationEqName 决定显示哪些表单项
    relationEqName: (e.attributes && e.attributes.EQUIP_TYPE) || e.equipType || '',
    classEnumId: e.classEnumId || '',  // 保留原始 classEnumId 供 getResourceView 兜底匹配
    GroupId: e.parentFixedAssetId || e.parentGroupId || e.GroupId || '',  // 所属分组 ID
    // 排序字段：老 ECC 默认 1000，树按 Sorter 升序
    Sorter: e.sorter || e.Sorter || '1000',
    ServerAddress: e.ipAddress || e.serverAddress || e.ServerAddress,
    // 旧 EccEquipment 契约为 EquipmentStatus：parseEquipmentData 按它统计正常/错误资源数，
    // 资源表 statusFormatter 按它渲染图标状态色；MonitorStatus 保留供树组件兜底链使用
    EquipmentStatus: e.status || e.monitorStatus || e.MonitorStatus || '',
    MonitorStatus: e.status || e.monitorStatus || e.MonitorStatus,
    // 设备"最后状态修改时间"（其监测器最近一次采集时刻的最大值，后端 tree 已输出）：
    // 缺失时 moment(undefined) 渲染成当前时刻，随 serverCurrentTime 轮询跳动
    LastModDateTime: e.lastModDateTime || e.LastModDateTime || '',
    IsDisable: normalizeDisabled(e.isDisable || e.isDisabled || e.IsDisable),
    EquipmentMonitor: monitors.map((mon) => adaptMonitorToLegacy(mon)),
  };
}

/** 转换单个监测器节点 */
export function adaptMonitorToLegacy(mon) {
  const m = mon || {};
  // specialProps：后端树接口返回 camelCase（specialProps），编辑监测器表单回填依赖
  const specialProps = m.specialProps || m.SpecialProperties || m.SpecialProps || '';
  return {
    RecId: m.recId || m.monitorId || m.RecId,
    Title: m.title || m.monitorName || m.nodeName || m.Title,
    MonitorType: m.monitorType || m.MonitorType,
    MonitorStatus: m.status || m.monitorStatus || m.MonitorStatus,
    MonitorValue: m.monitorValue || m.lastValue || m.MonitorValue || '',
    // isDisabled 可能是字符串 'false'（truthy），必须规范化成布尔，否则前端 `if (IsDisabled)` 误判禁用置灰
    IsDisable: normalizeDisabled(m.isDisable || m.isDisabled || m.IsDisable),
    // monitorInfoView 等页面用 IsDisabled（布尔）判断禁用，与 IsDisable 保持一致
    IsDisabled: normalizeDisabled(m.isDisable || m.isDisabled || m.IsDisable),
    // 父设备 ID：编辑监测器时 EditMonitorPanel 用 selectedNode.EquipmentId
    // 从设备树映射 deviceMonitorTreeMap[EquipmentId] 取设备节点
    EquipmentId: m.parentFixedAssetId || m.equipmentId || m.EquipmentId || '',
    GroupId: m.parentGroupId || m.groupId || m.GroupId || '',
    ServerAddress: m.targetHost || m.serverAddress || m.ServerAddress || '',
    // 编辑监测器表单回填字段（对齐旧 OData 树节点完整字段）
    SpecialProperties: specialProps,
    SpecialProps: specialProps,
    RefreshTime: m.refreshTime || m.RefreshTime || '',
    RefreshUnit: m.refreshUnit || m.RefreshUnit || '',
    // 最后更新时间（最近一次真实采集时刻）：监测器列表"最后更新"列直接渲染该值，
    // 缺失时 moment(undefined) 渲染成当前时刻，会随 serverCurrentTime 轮询重渲染而跳动
    LastModDateTime: m.lastModDateTime || m.LastModDateTime || '',
    StatusCreateTime: m.statusCreateTime || m.StatusCreateTime || '',
    MonitorTimeOut: m.timeout || m.monitorTimeOut || m.MonitorTimeOut || '',
    MonitorPackage: m.monitorPackage || m.MonitorPackage || '',
    TaskScheduler: m.taskScheduler || m.TaskScheduler || '',
    Description: m.description || m.Description || '',
    Sorter: m.sorter || m.Sorter || '',
    BelongID: m.belongId || m.BelongID || '',
  };
}

/**
 * 把新接口 /equipment/getById 的返回适配成旧扁平 PascalCase 结构。
 *
 * 新接口返回（attributes 嵌套）：
 *   { equipmentId, fixedAssetName, classEnumId, parentFixedAssetId,
 *     serialNumber, locatedAtFacilityId, facilityName, groupName,
 *     attributes: { IP_ADDRESS, MAC_ADDRESS, OS_TYPE, VENDOR, MODEL,
 *                   SSH_PORT, SSH_USER, SSH_PWD, SNMP_COMMUNITY, SNMP_VERSION,
 *                   SNMP_PORT, EQUIP_STATUS, CONTACT, DESCRIPTION } }
 *
 * 旧表单期望（扁平 PascalCase）：
 *   { RecId, Title, ServerAddress, EquipmentType, GroupId, EquipmentStatus,
 *     OS, Port, UserName, Passwd, Community, Edition, EquipmentModel, ... }
 *
 * @param {object} detail 新接口 getById 返回的对象
 * @param {function} [classEnumToType] classEnumId→旧EquipmentType 的映射函数
 * @returns {object} 旧扁平结构
 */
export function adaptEquipmentDetailToLegacy(detail, classEnumToType) {
  const d = detail || {};
  const a = d.attributes || {};
  return {
    RecId: d.equipmentId || d.RecId,
    Title: d.fixedAssetName || d.Title,
    ServerAddress: a.IP_ADDRESS || d.ipAddress || d.ServerAddress || '',
    EquipmentType: classEnumToType ? classEnumToType(d.classEnumId) : (d.EquipmentType || ''),
    // 设备真实类型码（EQUIP_TYPE，如 Equipment.Windows），供新增监测器按 /monitor/types 过滤
    equipType: a.EQUIP_TYPE || d.equipType || '',
    // 旧 UI 组件（resourceSetting_basicInfo_*）用 relationEqName 决定显示哪些表单项
    relationEqName: a.EQUIP_TYPE || d.equipType || '',
    GroupId: d.parentFixedAssetId || d.GroupId || '',
    EquipmentStatus: a.EQUIP_STATUS || d.equipStatus || d.EquipmentStatus || '',
    OS: a.OS_TYPE || d.OS || '',
    Port: a.SSH_PORT || d.Port || '',
    UserName: a.SSH_USER || d.UserName || '',
    Passwd: a.SSH_PWD || d.Passwd || '',
    Community: a.SNMP_COMMUNITY || d.Community || '',
    Edition: a.SNMP_VERSION || d.Edition || '',
    AuthenticationProtocol: a.SNMP_AUTH_PROTOCOL || d.AuthenticationProtocol || '',
    PrivacyProtocol: a.SNMP_PRIV_PROTOCOL || d.PrivacyProtocol || '',
    PrivacyPwd: a.SNMP_PRIV_PWD || d.PrivacyPwd || '',
    // 数据库设备：数据库名 / 连接数（编辑表单 resourceUtils.js 按 DATABASE_NAME/KeepaLives 回显）
    DATABASE_NAME: a.DATABASE_NAME || d.databaseName || '',
    KeepaLives: a.KEEP_ALIVES || d.keepAlives || '',
    // 数据库设备扩展参数：类型 / schema / 驱动 / URL / 凭据 / 端口
    DB_TYPE: a.DB_TYPE || d.dbType || '',
    Schemas: a.DB_SCHEMA || d.dbSchema || '',
    DB_SCHEMA: a.DB_SCHEMA || d.dbSchema || '',
    DB_DRIVER: a.DB_DRIVER || d.dbDriver || '',
    DB_URL: a.DB_URL || d.dbUrl || '',
    DB_USER: a.DB_USER || d.dbUser || '',
    DB_PASSWORD: a.DB_PASSWORD || d.dbPassword || '',
    DB_PORT: a.DB_PORT || d.dbPort || '',
    EquipmentModel: a.MODEL || d.EquipmentModel || '',
    VENDOR: a.VENDOR || d.VENDOR || '',
    MAC_ADDRESS: a.MAC_ADDRESS || '',
    CONTACT: a.CONTACT || '',
    Description: a.DESCRIPTION || d.description || d.Description || '',
    // 报警配置（/alarmRule/list 的 ruleId，编辑打开时回显下拉）
    AlarmConfig: a.ALARM_CONFIG || d.alarmConfig || '',
    serialNumber: d.serialNumber || '',
    facilityName: d.facilityName || '',
    groupName: d.groupName || '',
    locatedAtFacilityId: d.locatedAtFacilityId || '',
  };
}

/**
 * 把 OFBiz /monitor/types 返回的监测器模板项扁平化成旧前端期望的格式。
 *
 * 新接口返回（attributes 嵌套）：
 *   { fixedAssetId, fixedAssetName, attributes: {
 *     MONITOR_NAME, MONITOR_TABLE_NAME, MONITOR_TYPE,
 *     MONITOR_RANGE, IS_KEEPALIVE, IS_PASSIVE, ORIG_REC_ID
 *   }}
 *
 * 旧前端期望（扁平 PascalCase）：
 *   { RecId, MonitorName, MonitorTableName, MonitorType,
 *     MonitorRange, IsKeepalive, isPassive, description }
 *
 * 关键：旧前端用 MonitorTableName.substring(11) 取类型码
 * （"EccMonitor.Port" → "Port"），所以必须正确设置 MonitorTableName。
 * MONITOR_TYPE 在新系统里是中文类别描述（如 "SNMP Windows 服务器"），
 * 不是类型码，类型码必须从 MONITOR_TABLE_NAME 提取。
 *
 * @param {object} item OFBiz 返回的单个监测器模板项
 * @returns {object} 旧前端扁平格式
 */
export function adaptMonitorTypeItemToLegacy(item) {
  const i = item || {};
  const a = i.attributes || {};
  // MONITOR_TABLE_NAME 格式 "EccMonitor.Port"，去掉前缀得到类型码 "Port"
  const tableName = a.MONITOR_TABLE_NAME || i.MonitorTableName || '';
  const typeCode = tableName.startsWith('EccMonitor.')
    ? tableName.substring(11)
    : tableName;
  return {
    RecId: i.fixedAssetId || i.monitorId || i.RecId || a.ORIG_REC_ID || typeCode,
    MonitorName: a.MONITOR_NAME || i.fixedAssetName || i.MonitorName || '',
    MonitorTableName: tableName,
    // 类型码（如 "Port"），供 UI 组件严格 === 'Port' 等判断
    MonitorType: typeCode,
    monitorType: typeCode,
    // MONITOR_RANGE 格式 "Equipment.Windows"，供旧代码按设备类型过滤
    MonitorRange: a.MONITOR_RANGE || i.MonitorRange || '',
    IsKeepalive: a.IS_KEEPALIVE || i.IsKeepalive || 'false',
    isPassive: a.IS_PASSIVE === 'Y' || i.isPassive || false,
    description: a.DESCRIPTION || i.description || '',
  };
}

/**
 * 操作审计 VO -> 旧前端契约（operationAudit_action 消费）
 *
 * 新契约（后端 OperationAuditServices.buildAuditVo）：
 *   noteId/type/operator/module/operationType/targetId/targetObject/
 *   description/oldStatus/newStatus/status/ipAddress/association/localIp/
 *   changeDiff/operationTime
 * 旧契约（操作审计表格 9 列 + rowKey）：
 *   RecId/OperationType/OperationBo/OperationDesc/OperationAssociation/
 *   OperationResults('1'成功/'0'失败)/Operator/LocalIP/IPAddress/CreatedDateTime
 *
 * 注意：
 *   - operationType 存的是小写 key（如 add/edit），与旧 OData 语义一致，
 *     表格 render 用 OperationTypes.find(key==text) 转中文；
 *   - status 只有 SUCCESS/FAIL 两种，FAIL 映射 '0'（标红）；
 *   - operationTime 形如 "2026-08-29 16:30:00.0"，截 19 位去掉毫秒尾巴。
 *
 * @param {object} vo 后端审计记录
 * @returns {object} 旧前端行格式
 */
export function adaptOperationAuditToLegacy(vo) {
  const v = vo || {};
  // OpLogUtil 写入的大写 LOGIN/LOGOUT 规范化为小写 key，供表格 OperationTypes 字典转中文
  let opType = v.operationType || '';
  if (opType === 'LOGIN') opType = 'login';
  else if (opType === 'LOGOUT') opType = 'logout';
  return {
    RecId: v.noteId || '',
    OperationType: opType,
    OperationBo: v.module || v.targetObject || '',
    OperationDesc: v.description || '',
    OperationAssociation: v.association || '',
    // FAILED（OpLogUtil 写法）与 FAIL 同视为失败，'0' 供表格标红
    OperationResults: (v.status === 'FAIL' || v.status === 'FAILED') ? '0' : '1',
    Operator: v.operator || '',
    LocalIP: v.localIp || '',
    IPAddress: v.ipAddress || '',
    CreatedDateTime: (v.operationTime || '').substring(0, 19),
  };
}

